import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { 
    pendingVerification, 
    isAuthenticated,
    resendVerification, 
    checkEmailVerification,
    logout 
  } = useAuthStore();
  
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Redirect if already verified
  useEffect(() => {
    if (isAuthenticated && !pendingVerification) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, pendingVerification, navigate]);

  // Redirect to login if not in pending verification state
  useEffect(() => {
    if (!pendingVerification && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [pendingVerification, isAuthenticated, navigate]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-check verification status every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const verified = await checkEmailVerification();
      if (verified) {
        toast.success('Email verified! Redirecting to login...');
        // Logout and redirect to login so they can sign in fresh
        await logout();
        navigate('/login', { replace: true });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [checkEmailVerification, logout, navigate]);

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    
    setIsResending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Check your inbox.');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send email';
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckVerification = async () => {
    setIsChecking(true);
    try {
      const verified = await checkEmailVerification();
      if (verified) {
        toast.success('Email verified! Redirecting to login...');
        await logout();
        navigate('/login', { replace: true });
      } else {
        toast.error('Email not verified yet. Please check your inbox.');
      }
    } catch {
      toast.error('Failed to check verification status');
    } finally {
      setIsChecking(false);
    }
  };

  const handleBackToLogin = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8 text-center"
    >
      {/* Icon */}
      <div className="flex justify-center">
        <div className="w-20 h-20 bg-gradient-accent rounded-full flex items-center justify-center">
          <Mail className="w-10 h-10 text-white" />
        </div>
      </div>

      {/* Title */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary">
          Verify Your Email
        </h1>
        <p className="mt-3 text-text-muted max-w-md mx-auto">
          We've sent a verification link to your email address. 
          Please check your inbox and click the link to verify your account.
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-dark-200 rounded-xl p-6 text-left space-y-4">
        <h3 className="text-text-primary font-semibold">Next Steps:</h3>
        <ol className="list-decimal list-inside space-y-2 text-text-secondary">
          <li>Check your email inbox (and spam folder)</li>
          <li>Click the verification link in the email</li>
          <li>Return here and click "I've Verified" or wait for auto-redirect</li>
        </ol>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        <button
          onClick={handleCheckVerification}
          disabled={isChecking}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isChecking ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              I've Verified My Email
            </>
          )}
        </button>

        <button
          onClick={handleResendEmail}
          disabled={isResending || resendCooldown > 0}
          className="btn-secondary w-full flex items-center justify-center gap-2"
        >
          {isResending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : resendCooldown > 0 ? (
            <>
              <RefreshCw className="w-5 h-5" />
              Resend in {resendCooldown}s
            </>
          ) : (
            <>
              <RefreshCw className="w-5 h-5" />
              Resend Verification Email
            </>
          )}
        </button>
      </div>

      {/* Back to login */}
      <div className="pt-4 border-t border-dark-300">
        <button
          onClick={handleBackToLogin}
          className="text-text-muted hover:text-text-primary transition-colors flex items-center justify-center gap-2 mx-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>
      </div>

      {/* Auto-check notice */}
      <p className="text-sm text-text-muted">
        This page automatically checks for verification every 5 seconds.
      </p>
    </motion.div>
  );
}

